import React, {useEffect, useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {Tooltip} from '@/components/tooltips';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import Icon from '@/components/icons/Icon';

import axios from 'axios';
import {navigate} from '@/reach-router-compat';
import LoadingWrapper from '@/components/loading/LoadingWrapper';  
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import {buildAgentDashboardURL} from '@/utils/urlBuilder';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import useData from '@/utils/useData';
import TableComponent from '@/components/table/Table';
import {buildURL} from '@/utils/urlBuilder';
import AssignAgentSettingsModal from './AssignAgentSettingsModal';
import AssignAgentTeamSettingsModal from './AssignAgentTeamSettingsModal';
import {useStoreActions, useStoreState} from 'easy-peasy';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import chunk from 'lodash/chunk';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

interface Team {
    teamId: string;
}

interface Agent {
    agentId: number;
    agentName: string;
    agentExternalId: string;
    id: number;
    activeBetween: {
        start: string;
        end: string | null;
    };
}


interface UserData {
    permissions?: string[];
}

interface TeamData {
    id: number;
    name: string;
    shortname: string;
    teamAgents: Agent[];
}

const TeamMembersListView: React.FC<Team> = React.memo((team) => {
    const setSearchQueryState = async (setSearchQuery: (value: string) => void, value: string, setPage: (page: number) => void) => {
        setPage(1);
        setSearchQuery(value);
    };
    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);

    const [assignAgentModalOpen, setAssignAgentModalOpen] = useState<boolean>(false);
    const [selectedAgentId, setSelectedAgentId] = useState<number>(0);
    const [selectedAgentTeamId, setSelectedAgentTeamId] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    let pages: number[] = [1];

    let teamDetailUrl = '';
    if (team.teamId) {
        teamDetailUrl = buildURL('teamDetails', {team_id: team.teamId});
    }
    const [selectedTeam, loadingTeamData] = useData<TeamData>(teamDetailUrl, {useLocalStore: true});
    const setAgents = useStoreActions((actions: any) => actions.agents.store);
    let teamMembers = useStoreState((state: any) => state.agents.items) as Agent[];

    if (searchQuery) {
        teamMembers = teamMembers.filter((member: Agent) => member?.agentName?.includes(searchQuery));

        if (teamMembers.length > 0) {
            const paginatedTeamMembers = chunk(teamMembers, 30);
            pages = Array.from({length: paginatedTeamMembers.length}, (x, i) => i + 1);
            teamMembers = paginatedTeamMembers[currentPage - 1];
        }
    }

    useEffect(() => {
        if (!settingsModalOpen && selectedTeam) {
            const fetchUpdatedTeam = async () => {
                const response = await axios.get(teamDetailUrl);
                setAgents(response.data.teamAgents);
            };
            fetchUpdatedTeam();
        }
    }, [settingsModalOpen, selectedTeam, teamDetailUrl, setAgents]);

    const userCanAccessAgentDashboard = hasPermission('fs.agent.read', userData?.permissions, true);

    const editAgentInTeam = hasPermission('fs.agent.team_assignment.update', userData?.permissions, true);

    const canAddTeamMembers = hasPermission('fs.agent.team_assignment.create', userData?.permissions, true);

    const userCanAccessTeamsResource = hasPermission('fs.team.list', userData?.permissions, true);

    if (!userCanAccessTeamsResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_the_team_members.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openAssignAgentModal = (agentId: number, agentTeamId: number) => {
        setSelectedAgentId(agentId);
        setSelectedAgentTeamId(agentTeamId);
        setAssignAgentModalOpen(true);
    };

    const closeAssignAgentModal = () => {
        setSelectedAgentId(0);
        setSelectedAgentTeamId(0);
        setAssignAgentModalOpen(false);
    };

    const closeSettingsModal = () => {
        setSettingsModalOpen(false);
    }

    const active = currentPage;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const detailUrlGenerator = (rowData: Agent): string => {
        return buildAgentDashboardURL(rowData.agentId);
    };

    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentPiiRestricted = restrictedFieldKeys.includes('agent_pii');

    if (!loadingTeamData) {
        const columns = [
            {
                header: i18n.t('th_name.text'),
                accessorKey: 'agentName',
                cell: (cell: any) => {
                    if (agentPiiRestricted) {
                        return <RestrictedFieldIndicator />;
                    }
                    return <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />;
                },
            },
            {
                header: i18n.t('th_external.text'),
                accessorKey: 'agentExternalId',
                cell: (cell: any) => {
                    if (agentPiiRestricted) {
                        return <RestrictedFieldIndicator />;
                    }
                    return cell.getValue() || '';
                },
            },
            {
                header: i18n.t('th_started_team.text'),
                accessorKey: 'activeBetween.start',
            },
            {
                header: i18n.t('th_left_the.text'),
                cell: (cell: any) => {
                    const value = cell.getValue();
                    return value ? value : '-';
                },
                accessorKey: 'activeBetween.end',
            },
            {
                header: i18n.t('th_action.text'),
                cell: (cell: any) => (
                    <div className="d-flex align-items-center justify-content-end w-100">
                        <div className="d-inline-block mr-2">
                            {
                                userCanAccessAgentDashboard ? (
                                    <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            icon="mdi-eye"
                                            onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                            data-cy="view-team-member"
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div style={{
                                            display: 'inline-block',
                                            cursor: 'not-allowed',
                                        }}
                                        >
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                icon="mdi-eye"
                                                disabled
                                                style={{pointerEvents: 'none'}}
                                                data-cy="team-members-list-eye-outlinesecondary-btn" />
                                        </div>
                                    </Tooltip>
                                )
                            }
                        </div>

                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    data-cy="agent-settings-toggle"
                                    id="settings-dropdown-1"
                                />

                                <Dropdown.Menu align="end">
                                    {
                                        editAgentInTeam ? (
                                            <Dropdown.Item
                                                onClick={() => openAssignAgentModal(cell.row.original.agentId, cell.row.original.id)}
                                                data-cy="edit-team-member"
                                            >
                                                <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_edit_agent.text')}
                                            </Dropdown.Item>
                                        ) : (
                                            <Tooltip
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                placement="top"
                                            >
                                                <div
                                                    className="ml-auto"
                                                    style={{
                                                        cursor: 'not-allowed',
                                                        display: 'flex',
                                                    }}
                                                >
                                                    <Dropdown.Item
                                                        style={{pointerEvents: 'none'}}
                                                        disabled
                                                        data-cy="team-members-list-btn">
                                                        <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                    </Dropdown.Item>
                                                </div>
                                            </Tooltip>
                                        )
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                    </div>
                ),
            },
        ];

        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_the_team_members.text')} />
                <LoadingWrapper
                    loading={loadingTeamData}
                    loadingText={i18n.t('jt_loading_data_general.text')}
                    minHeight="400px"
                >
                    <Row>
                        <Col lg={3}>
                            <Form
                                className="mb-2 search-box"
                                onSubmit={(e: React.FormEvent) => {
                                    e.preventDefault();
                                }}
                            >
                                <Form.Control
                                    type="text"
                                    placeholder={i18n.t('es_search.text')}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                                    data-cy="team-search-field"
                                />
                            </Form>
                        </Col>
                        <Col lg={9} className="d-flex justify-content-end align-items-center">
                            {
                                canAddTeamMembers && selectedTeam ? (
                                    <Tooltip content={i18n.t('jt_add.text')} placement="top">
                                        <Button
                                            variant="info"
                                            size="sm"
                                            className='mb-2'
                                            icon="mdi-plus"
                                            onClick={() => setSettingsModalOpen(true)}
                                            data-cy="add-team-member-button"
                                        >
                                            {i18n.t('bn_add_team_member.text') || 'Add Team Member'}
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        content={canAddTeamMembers ? i18n.t('jt_loading_data_general.text') : i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                icon="mdi-plus"
                                                className='mb-2'
                                                disabled
                                                style={{pointerEvents: 'none'}}
                                                data-cy="add-team-member-button-disabled"
                                            >
                                                {i18n.t('bn_add_team_member.text') || 'Add Team Member'}
                                            </Button>
                                        </div>
                                    </Tooltip>
                                )
                            }
                        </Col>
                    </Row>

                    <>
                        <div className="card">
                            <div className="card-body">
                                    <TableComponent data={teamMembers} columns={columns} />
                                    <PaginationComponent
                                        pages={pages}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                    />
                            </div>
                        </div>
                        {
                            assignAgentModalOpen ? (
                                <AssignAgentSettingsModal
                                    modalOpen={assignAgentModalOpen}
                                    closeModal={closeAssignAgentModal}
                                    activeAgentId={selectedAgentId}
                                    activeTeamId={selectedTeam.id}
                                    activeAgentTeamId={selectedAgentTeamId}
                                /> as any
                            ) : null
                        }
                        {
                            settingsModalOpen && selectedTeam ? (
                                <AssignAgentTeamSettingsModal
                                    modalOpen={settingsModalOpen}
                                    closeModal={closeSettingsModal}
                                    activeTeamId={selectedTeam.id}
                                /> as any
                            ) : null
                        }
                    </>
                </LoadingWrapper>
            </>
        );
    }
});

export default TeamMembersListView; 